import React, { useEffect, useState } from 'react';
import { Route, RouteProps } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getDebitAccount as fetchDebitAccount } from 'stash/actions';
import type { Entities } from 'stash/types/redux/entities';
import type { ReduxDebitAccount } from 'stash/types/redux/entities/bank';
import EligibilityRoute from './EligibilityRoute';
import NextStepRoute from './NextStepRoute';
import { ButtonStandard, Text } from '@stashinvest/react-components';

export const DisabledFeaturesList = {
	CardReissue: 'cardReissue',
	CardActivation: 'cardActivation',
	MoneyTransfer: 'moneyTransfer',
	AtmLocator: 'atmLocator',
	PlaidInsights: 'plaidInsights',
	CashReload: 'cashReload',
	VirtualCard: 'virtualCard',
	DirectDeposit: 'directDepositInfo',
	AccountInfo: 'accountAndRoutingNumbers',
	Statements: 'statements',
	PinReset: 'pinReset',
	Travel: 'travelInfo',
	SpendingInsights: 'spendingInsights',
	StashSettings: 'stashSettings',
	StockBack: 'stockBack',
	DataSharing: 'dataSharing',
	PayBills: 'payBills',
	RecurringTransfer: 'recurringTransfer',
	MRDC: 'mrdc',
	Envelopes: 'envelopes',
	CardPerks: 'cardPerks',
	CardManagement: 'cardManagement',
	DebitFund: 'debitFund',
	CreateDispute: 'createDispute',
} as const;

type BankRouteProps = RouteProps & {
	feature?: string;
	redirect?: string;
};

type BankRouteState = {
	entities?: Entities;
};

export const selectBankRouteDebitAccounts = (
	state: BankRouteState
): ReduxDebitAccount[] | undefined => {
	const debitAccounts = state?.entities?.bank?.debit_accounts;
	return Array.isArray(debitAccounts) ? debitAccounts : undefined;
};

export const selectBankRouteState = (state: BankRouteState) => {
	const account = selectBankRouteDebitAccounts(state);
	return {
		disabledFeatures: account?.[0]?.disabledFeatures,
		account,
	};
};

export function BankRoute(props: BankRouteProps) {
	const dispatch = useDispatch();
	const account = useSelector(selectBankRouteDebitAccounts);
	const [error, setError] = useState(false);

	useEffect(() => {
		if (!account) {
			dispatch(fetchDebitAccount()).catch(() => {
				setError(true);
			});
		}
	}, [account, dispatch]);

	if (error) {
		return (
			<div className="flex min-h-[95vh] flex-col items-center justify-center">
				<div className="w-full max-w-[28rem] text-center">
					<Text
						tag="h1"
						textStyle="headerSmall"
						color="text-onSurface"
						className="mb-contentMedium !font-bold"
					>
						Could not load banking
					</Text>
					<Text
						textStyle="bodyMediumStandard"
						color="text-onSurfaceVariant"
						className="mb-contentMedium"
					>
						There was a connection issue. Please try again.
					</Text>
					<ButtonStandard fullWidth onClick={() => window.location.reload()}>
						Try again
					</ButtonStandard>
				</div>
			</div>
		);
	}

	if (account && account.length === 0) {
		return (
			<Route {...props}>
				<EligibilityRoute />
			</Route>
		);
	}

	if (account && account.length) {
		return (
			<Route {...props}>
				<NextStepRoute
					account={account}
					{...props}
				/>
			</Route>
		);
	}

	return null;
}

export default BankRoute;
